import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { API } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Coins, Download, TrendingUp, Users, ArrowLeft, ShieldCheck } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString();

export default function BusinessPlan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans));
    api.get("/settings").then(({ data }) => setSettings(data.settings));
  }, []);

  const levels = (settings.referral_levels || []).sort((a, b) => a.level - b.level);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Coins className="h-7 w-7 text-emerald-400" />
            <span className="text-xl font-extrabold">ERAN <span className="text-emerald-400">USD</span></span>
          </Link>
          <Button data-testid="back-btn" variant="outline" onClick={() => navigate(user ? "/dashboard" : "/")}
            className="rounded-full border-slate-700 hover:bg-white/5">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Business Plan</h1>
            <p className="text-slate-400 mt-2 max-w-xl">
              Full ERAN USD earning model — investment plans, daily ROI, multi-level referral income
              and withdrawal rules. Download the official PDF below.
            </p>
          </div>
          <a href={`${API}/business-plan.pdf`} target="_blank" rel="noopener noreferrer" data-testid="download-plan-pdf-btn">
            <Button className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-6 w-full sm:w-auto">
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          </a>
        </motion.div>

        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-emerald-400" />
          <h2 className="text-xl font-bold">Investment Plans</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.map((p) => (
            <div key={p.id} className="card-surface rounded-2xl p-6" data-testid={`plan-info-${p.id}`}>
              <div className="text-sm text-slate-400 uppercase tracking-wider">{p.name}</div>
              <div className="text-4xl font-extrabold font-mono text-emerald-400 my-3">{p.daily_roi_pct}%</div>
              <div className="text-xs text-slate-500 mb-4">daily ROI · {p.duration_days} days</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Range</span><span className="font-mono">${fmt(p.min_amount)} – ${fmt(p.max_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Total ROI</span><span className="font-mono text-emerald-400">{(p.daily_roi_pct * p.duration_days).toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Principal</span><span>Returned at maturity</span></div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-surface rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><Users className="h-5 w-5 text-emerald-400" /><h2 className="text-xl font-bold">Referral Income</h2></div>
            <div className="space-y-3">
              {levels.map((l) => (
                <div key={l.level} className="flex items-center justify-between border border-border rounded-xl px-4 py-3">
                  <span className="text-sm">Level {l.level}</span>
                  <span className="font-mono text-emerald-400 font-semibold">{l.pct}%</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4">Commission is credited automatically on every deposit made by your network.</p>
          </div>

          <div className="card-surface rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-emerald-400" /><h2 className="text-xl font-bold">Deposit &amp; Withdrawal Rules</h2></div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>• Networks: USDT-TRC20 (Tron) &amp; USDT-BEP20 (BSC)</li>
              <li>• Minimum deposit: <span className="font-mono">{settings.min_deposit} USDT</span></li>
              <li>• Minimum withdrawal: <span className="font-mono">{settings.min_withdrawal} USDT</span></li>
              <li>• Maximum withdrawal: <span className="font-mono">{fmt(settings.max_withdrawal)} USDT</span></li>
              <li>• Withdrawal fee: <span className="font-mono">{settings.withdrawal_fee_pct}%</span></li>
              <li>• Withdrawals are admin-approved &amp; paid on-chain with a TXID</li>
              <li>• Real on-chain USDT only — no fake balances</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
